import { useState, useEffect, useRef, useMemo, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "@/contexts/UserContext";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useMyMembership } from "@/hooks/useMyMembership";
import { useUserRole } from "@/hooks/useUserRole";
import { VerifyCTA } from "@/components/experts/VerifyCTA";
import { logger } from "@/utils/logger";
import { useSEO } from "@/hooks/useSEO";
import { SEOHead, generateWebSiteSchema, generateOrganizationSchema } from "@/components/seo/SEOHead";
import { SITE_NAME } from "@/config/seo";
import { DOMAIN_DEFAULTS } from "@/lib/domainDefaults";
import { BRAZIL_LOCATION_ID } from "@/constants/branding";
import { INTERNATIONAL_LOCATION_ID } from "@/constants/config";
import { generateLocalBusinessSchema, generateFAQPageSchema, generateSpeakableSchema } from "@/lib/seoSchemas";
import { HeroChat } from "@/components/HeroChat";
import { HomeInsightsSection } from "@/components/sections/HomeInsightsSection";


import { MapSection } from "@/components/sections/MapSection";
const NotFound = lazy(() => import("@/pages/NotFound"));

import { LPages } from "@/components/i18n";
import { useLocalizations } from "@/hooks/useLocalizations";
import { useLocale } from "@/contexts/LocaleContext";
import { HomePageSkeleton } from "@/components/skeletons";
import { Card, CardContent } from "@/components/ui/card";
import { GRIDS, RESPONSIVE, LAYOUTS, SPACING } from "@/design-system";
import { cn } from "@/lib/utils";
import { DEFAULT_REGIONAL_LOCALE } from "@/constants/localization";




interface LocationDetail {
  location_id: string;
  name: string;
  iso_alpha2: string;
  iso_alpha3?: string;
  slug: string;
  native_slug?: string;
  main_language?: string;
  short_name?: string;
  other_names?: string;
  coordinates?: string;
  summary?: string;
}

interface LocalizedSEOData {
  meta_title: string | null;
  meta_description: string | null;
  translated_body: string | null;
  translated_body_short: string | null;
  faq_items: Array<{ q: string; a: string }> | null;
  aio_snippet: string | null; // speakable_content is derived from aio_snippet
}

interface HomePageProps {
  locationSlug?: string;
}



const HomePage = ({ locationSlug }: HomePageProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: membership } = useMyMembership();
  const hasPaidMembership = !!membership;
  const { isSuperAdmin } = useUserRole();
  const { setCurrentLocation, updateLocation } = useUserContext();
  const { settings } = useLocale();
  const { lPages, lCommon } = useLocalizations();

  // Location state
  const [location, setLocation] = useState<LocationDetail | null>(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const hasLoadedOnce = useRef(false);
  const [localizedSEO, setLocalizedSEO] = useState<LocalizedSEOData | null>(null);
  const [hreflangLinks, setHreflangLinks] = useState<Array<{ lang: string; url: string }>>([]);




  // SEO data with hook
  const seoData = useSEO({
    title: lPages('home.seo.title', { defaultValue: `${SITE_NAME} | Tax AI, Expert-Verified` }),
    description: lPages('home.seo.description', { defaultValue: 'Connect with a network of tax and compliance professionals, access essential resources, and stay informed on the latest industry developments.' }),
    schema: {
      ...generateWebSiteSchema(),
      ...generateOrganizationSchema(),
    },
  });

  // Fetch location details when locationSlug changes
  useEffect(() => {
    const fetchLocation = async () => {
      if (!locationSlug) {
        // On tributario.com.br, root "/" IS the Brazil page — materialize it
        if (DOMAIN_DEFAULTS.isTributario) {
          setLoadingLocation(true);
          try {
            const { data: brazilData } = await supabase
              .from('locations')
              .select('location_id, name, iso_alpha2, iso_alpha3, native_slug, slug, main_language, short_name, other_names, coordinates, summary')
              .eq('location_id', parseInt(BRAZIL_LOCATION_ID))
              .single();
            
            if (brazilData) {
              const brazilDetail: LocationDetail = {
                ...brazilData,
                slug: brazilData.native_slug || brazilData.slug || 'brasil',
              };
              setLocation(brazilDetail);
              // Use 'navigation' source — not user_selection, to avoid state pollution
              updateLocation(BRAZIL_LOCATION_ID, 'navigation');

              // Fetch localized SEO for Brazil in current locale
              const currentLocale = settings?.locale || 'pt-BR';
              const { data: seoResult } = await supabase
                .from('localization_metadata')
                .select('meta_title, meta_description, translated_body, translated_body_short, faq_items, aio_snippet')
                .eq('source_type', 'location')
                .eq('source_id', BRAZIL_LOCATION_ID)
                .eq('locale', currentLocale)
                .maybeSingle();
              
              if (seoResult) {
                setLocalizedSEO({
                  meta_title: seoResult.meta_title,
                  meta_description: seoResult.meta_description,
                  translated_body: seoResult.translated_body,
                  translated_body_short: seoResult.translated_body_short,
                  faq_items: seoResult.faq_items as Array<{ q: string; a: string }> | null,
                  aio_snippet: seoResult.aio_snippet,
                });
              } else {
                setLocalizedSEO(null);
              }
            }
          } catch (error) {
            logger.error('Failed to fetch Brazil location for tributario root', { error });
          } finally {
            setLoadingLocation(false);
            hasLoadedOnce.current = true;
          }
          return;
        }
        
        // Non-tributario: root "/" is global mode
        setLocation(null);
        setLocalizedSEO(null);
        return;
      }

      setLoadingLocation(true);
      try {
        let locationId: string | null = null;
        let locationData: {
          location_id: string;
          name: string;
          iso_alpha2: string | null;
          iso_alpha3: string | null;
          native_slug: string | null;
          slug: string | null;
          main_language: string | null;
          short_name: string | null;
          other_names: string | null;
          coordinates: string | null;
          summary: string | null;
        } | null = null;

        // 1. First try locations table (English slugs - single source of truth)
        const { data: locBySlug } = await supabase
          .from('locations')
          .select('location_id, name, iso_alpha2, iso_alpha3, native_slug, slug, main_language, short_name, other_names, coordinates, summary')
          .or(`slug.eq.${locationSlug},native_slug.eq.${locationSlug}`)
          .maybeSingle();

        if (locBySlug) {
          locationId = locBySlug.location_id;
          locationData = locBySlug;
        } else {
          // 2. Check localization_metadata for non-English translations (source_type = 'location')
          const { data: translationData } = await supabase
            .from('localization_metadata')
            .select('source_id')
            .eq('source_type', 'location')
            .eq('translated_slug', locationSlug)
            .limit(1)
            .maybeSingle();

          if (translationData) {
            locationId = translationData.source_id;
            // Fetch full location details
            const { data: loc } = await supabase
              .from('locations')
              .select('location_id, name, iso_alpha2, iso_alpha3, native_slug, slug, main_language, short_name, other_names, coordinates, summary')
              .eq('location_id', parseInt(locationId))
              .single();
            locationData = loc;
          }
        }

        if (!locationId || !locationData) {
          setLocation(null);
          setLocalizedSEO(null);
          setLoadingLocation(false);
          return;
        }

        const locationDetail: LocationDetail = {
          ...locationData,
          slug: locationSlug,
        };

        setLocation(locationDetail);
        updateLocation(locationData.location_id?.toString() || INTERNATIONAL_LOCATION_ID, 'navigation');

        // Fetch localized SEO data for current locale from localization_metadata (source_type = 'location')
        const currentLocale = settings?.locale || DEFAULT_REGIONAL_LOCALE;
        const { data: seoData } = await supabase
          .from('localization_metadata')
          .select('meta_title, meta_description, translated_body, translated_body_short, faq_items, aio_snippet')
          .eq('source_type', 'location')
          .eq('source_id', locationId.toString())
          .eq('locale', currentLocale)
          .maybeSingle();

        if (seoData) {
          setLocalizedSEO({
            meta_title: seoData.meta_title,
            meta_description: seoData.meta_description,
            translated_body: seoData.translated_body,
            translated_body_short: seoData.translated_body_short,
            faq_items: seoData.faq_items as Array<{ q: string; a: string }> | null,
            aio_snippet: seoData.aio_snippet,
          });
        } else {
          setLocalizedSEO(null);
        }

        // Generate hreflang links
        const links: Array<{ lang: string; url: string }> = [];
        
        // Add English link from locations.slug
        if (locationData.slug) {
          links.push({
            lang: 'en-US',
            url: `${window.location.origin}/${locationData.slug}`,
          });
        }

        // Add non-English links from localization_metadata (source_type = 'location')
        const { data: slugTranslations } = await supabase
          .from('localization_metadata')
          .select('locale, translated_slug')
          .eq('source_type', 'location')
          .eq('source_id', locationId.toString());

        if (slugTranslations) {
          slugTranslations.forEach(t => {
            links.push({
              lang: t.locale,
              url: `${window.location.origin}/${t.translated_slug}`,
            });
          });
        }
        setHreflangLinks(links);

      } catch (error) {
        logger.error('Failed to fetch location', { error });
        setLocation(null);
        setLocalizedSEO(null);
      } finally {
        setLoadingLocation(false);
        hasLoadedOnce.current = true;
      }
    };

    fetchLocation();
  }, [locationSlug, updateLocation, settings?.locale]);




  // Show skeleton during location data fetch (router-rendered page, no Suspense)
  if (locationSlug && loadingLocation && !hasLoadedOnce.current) {
    return <HomePageSkeleton />;
  }

  // Not found state for location pages - use styled NotFound component
  if (locationSlug && !location && !loadingLocation) {
    return <Suspense fallback={null}><NotFound entityType="page" backLink="/" /></Suspense>;
  }

  // Dynamic SEO for location pages - strict locale matching, no English fallback
  // SOT: localization_metadata for entity pages, home.seo.title for non-location homepage
  const pageTitle = location 
    ? (localizedSEO?.meta_title ?? null)
    : seoData.title;
  const pageDescription = location 
    ? (localizedSEO?.meta_description ?? localizedSEO?.translated_body_short ?? null)
    : seoData.description;

  // Build schema array for location pages
  const buildLocationSchemas = () => {
    if (!location) return null;
    
    const schemas: Record<string, unknown>[] = [];
    
    // Local business schema
    schemas.push(generateLocalBusinessSchema({
      name: location.name,
      description: pageDescription,
      url: `${window.location.origin}/${location.native_slug || location.slug}`,
      country: location.name,
      services: [
        lCommon('services.tax_consulting', { defaultValue: 'Tax Consulting' }),
        lCommon('services.tax_planning', { defaultValue: 'Tax Planning' }),
        lCommon('services.international_tax', { defaultValue: 'International Tax' }),
        lCommon('services.transfer_pricing', { defaultValue: 'Transfer Pricing' }),
        lCommon('services.tax_compliance', { defaultValue: 'Tax Compliance' })
      ]
    }));
    
    // FAQ schema if faq_items exist
    if (localizedSEO?.faq_items && localizedSEO.faq_items.length > 0) {
      schemas.push(generateFAQPageSchema(
        localizedSEO.faq_items.map(item => ({
          question: item.q,
          answer: item.a
        }))
      ));
    }
    
    // Speakable schema for voice/AI assistants - derived from aio_snippet
    if (localizedSEO?.aio_snippet) {
      schemas.push(generateSpeakableSchema({
        cssSelectors: ['#hero-heading', '#hero-subheading', '#location-description']
      }));
    }
    
    return schemas;
  };

  return (
    <>
      {location ? (
        <SEOHead
          title={pageTitle}
          description={pageDescription}
          canonicalUrl={`${window.location.origin}/${location.native_slug || location.slug}`}
          hreflangLinks={hreflangLinks}
          schema={buildLocationSchemas()}
          locale={(settings.locale || settings.language).replace('-', '_')}
        />
      ) : (
        <SEOHead {...seoData} />
      )}
      <div className="flex-1 bg-muted/30">
        {/* Hero Section - same keys for both modes */}
        <section className={LAYOUTS.heroSection}>
          <h1 id="hero-heading" className={cn(
            SPACING.marginBottom6,
            RESPONSIVE.titleSection + ' text-foreground'
          )}>
            <LPages id="home.hero.title" defaultValue='What is your tax challenge?' />
          </h1>
          <p id="hero-subheading" className="text-lg md:text-xl text-muted-foreground mb-12">
            <LPages
              id="home.hero.subtitle"
              defaultValue='Tax AI, Expert-Verified' />
          </p>

          {/* Chat Interface - preselected location for location pages */}
          <HeroChat preselectedLocation={location?.iso_alpha2} />
        </section>



        {/* Insights Section - location-aware */}
        <HomeInsightsSection
          locationId={location?.location_id || null}
          locationName={location ? location.short_name || location.name : undefined}
          locationCountryCode={location?.iso_alpha2}
          locationDescription={location ? localizedSEO?.translated_body || location.summary : null}
        />

        {/* Experts Worldwide Map Section - zoomed for location pages */}
        <MapSection
          title={<LPages id="home.map.title" defaultValue='Expert Network' />}
          subtitle={<LPages id="home.map.subtitle" defaultValue='Explore our network of vetted experts everywhere' />}
          locationCode={location?.iso_alpha2}
          locationCoordinates={location?.coordinates}
        />

      </div>
    </>
  );
};

export default HomePage;
